import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import TradifyForm from "./TradifyForm";

const QuoteContext = createContext({ openQuote: () => {} });
export const useQuote = () => useContext(QuoteContext);

export function QuoteProvider({ children }) {
  const [state, setState] = useState({ open: false, service: "Not Sure", offer: false });

  const openQuote = useCallback((service = "Not Sure", offer = false) => {
    setState({ open: true, service, offer });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    if (!state.open) return undefined;
    document.documentElement.style.overflow = "hidden";
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [state.open, close]);

  return (
    <QuoteContext.Provider value={{ openQuote }}>
      {children}
      <AnimatePresence>
        {state.open && (
          <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-6">
            <motion.div
              className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              aria-hidden="true"
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Request a quote"
              data-testid="quote-modal"
              className="relative max-h-[90dvh] w-full overflow-y-auto overscroll-contain rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl sm:max-w-xl sm:rounded-3xl sm:p-8"
              initial={{ y: 48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 48, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                type="button"
                onClick={close}
                data-testid="quote-modal-close"
                aria-label="Close quote form"
                className="sticky top-4 z-10 -mb-11 ml-auto flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-ink/70 shadow-md transition-colors duration-200 hover:bg-sand"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>

              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">
                {state.offer ? "Multi-rail offer" : "Get a quote"}
              </p>
              <h3 className="mt-2 pr-10 font-display text-2xl font-bold text-charcoal sm:text-3xl">
                {state.offer ? "One visit. Every rail sorted." : "Tell us what you need."}
              </h3>
              <p className="mt-2 text-sm text-ink/60">
                {state.offer
                  ? "Two or more rails installed in one visit keeps costs down — tell us what you need and we'll price the bundle."
                  : "Grab rails, hand rails or rubber ramps — supplied and professionally installed across Perth."}
              </p>
              <div className="mt-6">
                <TradifyForm testid="modal-tradify-form" />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </QuoteContext.Provider>
  );
}
